import type { CNSConversation } from "@/models/cns";
import {
  deriveConversationSessionStatus,
  type ConversationSessionStatus,
  type DeriveConversationSessionStatusInput,
} from "@/lib/conversationSessionStatus";
import { formatHubChipLabel } from "@/lib/hubChipLabels";

export {
  deriveConversationSessionStatus,
  type ConversationSessionStatus,
  type DeriveConversationSessionStatusInput,
};

export const CONVERSATION_SESSION_STATUS_LABEL: Record<
  ConversationSessionStatus,
  string
> = {
  running: "Running",
  waiting: "Needs you",
  idle: "Idle",
};

const SECONDARY_LINE_MAX = 48;

export interface ConversationRowMetaInput
  extends Pick<
    CNSConversation,
    | "attentionHint"
    | "currentStage"
    | "lastAgentActionLine"
    | "primaryArtifactLabel"
    | "primaryArtifactStatus"
  > {}

export interface ConversationRowMetaOptions {
  isRunningLocally?: boolean;
  hasNextStep?: boolean;
  /** Max length for truncated secondary line (rail uses 48). */
  secondaryLineMax?: number;
}

export interface ConversationRowMeta {
  sessionStatus: ConversationSessionStatus;
  sessionStatusLabel: string;
  secondaryLine: string | null;
  secondaryLineFull: string | null;
  stageLabel: string | null;
  artifactStatusLabel: string | null;
  artifactLabel: string | null;
}

function truncateMeta(value: string, max: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

/**
 * Derives display meta for conversation rows on project home, rail, and list cards.
 * Secondary line priority: attentionHint → running label → stage → lastAgentActionLine (not when idle).
 */
export function getConversationRowMeta(
  conversation: ConversationRowMetaInput,
  opts?: ConversationRowMetaOptions,
): ConversationRowMeta {
  const isRunningLocally = opts?.isRunningLocally ?? false;
  const secondaryLineMax = opts?.secondaryLineMax ?? SECONDARY_LINE_MAX;

  const sessionStatus = deriveConversationSessionStatus({
    attentionHint: conversation.attentionHint,
    isRunningLocally,
    hasNextStep: opts?.hasNextStep ?? false,
  });

  const sessionStatusLabel = CONVERSATION_SESSION_STATUS_LABEL[sessionStatus];

  const stageLabel = conversation.currentStage?.trim()
    ? formatHubChipLabel(conversation.currentStage)
    : null;
  const artifactStatusLabel = conversation.primaryArtifactStatus?.trim()
    ? formatHubChipLabel(conversation.primaryArtifactStatus)
    : null;
  const artifactLabel = conversation.primaryArtifactLabel?.trim() || null;

  const hint = conversation.attentionHint?.trim();
  if (hint) {
    return {
      sessionStatus,
      sessionStatusLabel,
      secondaryLine: truncateMeta(hint, secondaryLineMax),
      secondaryLineFull: hint,
      stageLabel,
      artifactStatusLabel,
      artifactLabel,
    };
  }

  if (isRunningLocally) {
    return {
      sessionStatus,
      sessionStatusLabel,
      secondaryLine: CONVERSATION_SESSION_STATUS_LABEL.running,
      secondaryLineFull: CONVERSATION_SESSION_STATUS_LABEL.running,
      stageLabel,
      artifactStatusLabel,
      artifactLabel,
    };
  }

  if (stageLabel) {
    return {
      sessionStatus,
      sessionStatusLabel,
      secondaryLine: truncateMeta(stageLabel, secondaryLineMax),
      secondaryLineFull: stageLabel,
      stageLabel,
      artifactStatusLabel,
      artifactLabel,
    };
  }

  const actionLine = conversation.lastAgentActionLine?.trim();
  if (actionLine && sessionStatus !== "idle") {
    return {
      sessionStatus,
      sessionStatusLabel,
      secondaryLine: truncateMeta(actionLine, secondaryLineMax),
      secondaryLineFull: actionLine,
      stageLabel,
      artifactStatusLabel,
      artifactLabel,
    };
  }

  return {
    sessionStatus,
    sessionStatusLabel,
    secondaryLine: null,
    secondaryLineFull: null,
    stageLabel,
    artifactStatusLabel,
    artifactLabel,
  };
}

export function sessionStatusBadgeClass(
  status: ConversationSessionStatus,
): string {
  switch (status) {
    case "running":
      return "border-transparent bg-sky-500/15 text-sky-950";
    case "waiting":
      return "border-transparent bg-amber-500/15 text-amber-950";
    case "idle":
      return "border-transparent bg-muted text-muted-foreground";
  }
}

export function sessionStatusDotClass(status: ConversationSessionStatus): string {
  switch (status) {
    case "running":
      return "bg-sky-500";
    case "waiting":
      return "bg-amber-500";
    case "idle":
      return "bg-muted-foreground/40";
  }
}
